import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, categories, getRelatedArticles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug: articleSlug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(articleSlug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [articleSlug]);

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article.slug, article.category, 3);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteConfig.url}/article/${article.slug}`,
    },
    articleSection: category?.name || '',
    keywords: article.tags.join(','),
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    name: article.title,
    url: `${siteConfig.url}/article/${article.slug}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <article className="px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              {/* Breadcrumb */}
              <div className="mb-6">
                <Breadcrumb
                  items={[
                    { label: category?.name || '分类', href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>

              {/* Article Header */}
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/category/${article.category}`}
                    className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap"
                  >
                    {category?.name || '未分类'}
                  </Link>
                  {article.isHot && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-accent-100 text-accent-700 rounded-full">HOT</span>
                  )}
                  {article.isNew && (
                    <span className="px-2 py-0.5 text-xs font-semibold bg-primary-100 text-primary-700 rounded-full">NEW</span>
                  )}
                  <span className="text-xs text-foreground-500">{formatDate(article.publishedAt)}</span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.viewCount >= 10000
                      ? `${(article.viewCount / 10000).toFixed(1)}万`
                      : article.viewCount}{' '}
                    阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {article.commentCount} 评论
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex items-center gap-2 mb-10 flex-wrap">
                <span className="text-sm text-foreground-500">
                  <i className="ri-price-tag-3-line mr-1"></i>标签：
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 py-4 border-t border-background-200/70 mb-12">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-500 transition-colors">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-500 transition-colors">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-600 hover:text-primary-500 transition-colors">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="px-4 md:px-6 py-12 bg-background-100">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="font-heading text-lg font-bold text-foreground-950">
                    相关爆料
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <ArticleCard
                      key={related.id}
                      slug={related.slug}
                      title={related.title}
                      excerpt={related.excerpt}
                      coverImage={related.coverImage}
                      category={related.category}
                      categoryName={category?.name || ''}
                      author={related.author}
                      publishedAt={related.publishedAt}
                      tags={related.tags}
                      isHot={related.isHot}
                      isNew={related.isNew}
                      viewCount={related.viewCount}
                      commentCount={related.commentCount}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}